import { cn } from '@/lib';
import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { tv } from 'tailwind-variants';
import { Panel, PanelFooter } from '../Panel';
import { Icon } from '../ui/Icon';
import { HeaderButton } from './HeaderButton';
import { useMenuContext } from './hooks';
import { MenuItem } from './MenuItem';
import { MenuRoute } from './types';

const itemVariants = tv({
    base: 'animate-in fade-in fill-mode-backwards space-y-2 empty:hidden',
    variants: {
        direction: {
            forwards: 'slide-in-from-right-[100px]',
            backwards: 'slide-in-from-left-[100px]',
        },
    },
});
const animationStagger = 0.015;
const AnimationContainer = ({ children, index = 0 }: { children: ReactNode; index?: number }) => {
    const { direction } = useMenuContext();
    return (
        <li className={cn(itemVariants({ direction }))} style={{ animationDelay: `${index * animationStagger}s` }}>
            {children}
        </li>
    );
};

interface MenuProps {
    routes: MenuRoute[];
    defaultTitle?: string;
    onBack?: () => void;
    onClose?: () => void;
    onNavigate?: (path: string[]) => void;
}

// Component to render a single route as a menu item
const RouteMenuItem = ({ route, index }: { route: MenuRoute; index: number }) => {
    const { navigateTo, navigateByPath, path, activeView } = useMenuContext();

    const handleClick = () => {
        route.onClick?.();

        // Navigate only if:
        // 1. There's a view (to show a specific view)
        // 2. OR it's a parent route (has path but no onClick) - to show children
        // Routes with onClick but no view should NOT navigate (just update state)
        if (route.view) {
            const view = typeof route.view === 'function' ? route.view() : route.view;
            navigateTo(route.label, view);
        } else if (route.path.length > 0 && !route.onClick) {
            // Parent route without onClick - navigate to show children
            navigateByPath(route.path);
        }
    };

    // Check if route should be hidden
    const isHidden = typeof route.hidden === 'function' ? route.hidden() : (route.hidden ?? false);
    if (isHidden) return null;

    // Check if route is active
    const isActive = typeof route.active === 'function' ? route.active() : (route.active ?? false);

    // Check if we should hide this route based on current navigation state
    const shouldHide =
        (route.view && path.includes(route.label)) || // Currently active view
        (path.length > 0 && route.path.length === 0 && !activeView); // Nested navigation, not a nested route

    if (shouldHide) return null;

    // Render custom children if provided
    const customChildren = route.children
        ? typeof route.children === 'function'
            ? route.children(route)
            : route.children
        : null;

    return (
        <AnimationContainer index={index}>
            <MenuItem
                title={route.label}
                label={route.label}
                description={route.description || ''}
                icon={route.icon}
                active={isActive}
                onClick={handleClick}
                className={route.className}
            >
                {customChildren}
            </MenuItem>
        </AnimationContainer>
    );
};

export const Menu = ({ routes, defaultTitle = 'Global Edits', onBack, onClose, onNavigate }: MenuProps) => {
    const {
        path,
        activeView,
        navigationRequest,
        goBack: contextGoBack,
        getChildRoutes,
        registerRoutes,
        routes: contextRoutes,
    } = useMenuContext();

    // Register routes when they change - use ref to prevent infinite loops
    const routesRef = useRef<string>('');
    useEffect(() => {
        // Serialize routes to detect actual changes, not just reference changes
        const routesKey = JSON.stringify(routes.map(r => ({ id: r.id, path: r.path, active: r.active })));
        if (routes.length > 0 && routesRef.current !== routesKey) {
            routesRef.current = routesKey;
            registerRoutes(routes);
        }
    }, [routes]);

    // Call onNavigate when navigation changes
    const onNavigateRef = useRef(onNavigate);
    useEffect(() => {
        onNavigateRef.current = onNavigate;
    }, [onNavigate]);

    useEffect(() => {
        onNavigateRef.current?.(path);
    }, [path]);

    const handleGoBack = () => {
        contextGoBack();
        onBack?.();
    };

    const handleClose = onClose;
    const currentTitle = path[path.length - 1] || navigationRequest?.label || defaultTitle;

    // Get routes to display based on current path
    const routesToDisplay = useMemo(() => {
        if (activeView) return [];

        // Always use context routes (which are stable) - fallback to props only if context is empty
        const routesToUse = contextRoutes.length > 0 ? contextRoutes : routes;

        // If we're at root (path is empty), show root-level routes
        // Show routes with path.length === 0 (root routes) or path.length === 1 (first-level routes)
        // Routes with view are clickable items that navigate to a view
        if (path.length === 0) {
            return routesToUse.filter(r => r.path.length === 0 || r.path.length === 1);
        }

        // Build parent path by finding routes that match each label in path
        const parentPath: string[] = [];
        for (let i = 0; i < path.length; i++) {
            const segmentLabel = path[i];
            if (!segmentLabel) {
                continue;
            }

            // Find route with matching label - try to match at current depth
            const matchingRoute = routesToUse.find(r => {
                // Route should have path length matching current depth
                if (r.path.length !== i + 1) return false;
                // Previous path segments should match
                if (i > 0 && !r.path.slice(0, i).every((seg, idx) => seg === parentPath[idx])) return false;
                // Label should match
                return r.label === segmentLabel;
            });

            if (matchingRoute && matchingRoute.path.length > i) {
                const pathSegment = matchingRoute.path[i];
                if (pathSegment) {
                    parentPath.push(pathSegment);
                }
            } else {
                // Fallback: convert label to path segment (kebab-case)
                parentPath.push(segmentLabel.toLowerCase().replace(/\s+/g, '-'));
            }
        }

        // Get child routes for the current parent path
        return getChildRoutes(parentPath);
    }, [path, contextRoutes, activeView, getChildRoutes]);

    return (
        <>
            <div className='border-neutral-subtle text-md flex flex-row items-center justify-between gap-2 border-b p-4 font-bold'>
                <HeaderButton onClick={path.length > 0 ? handleGoBack : undefined} label={currentTitle} />

                <button
                    onClick={handleClose}
                    className='inline-flex cursor-pointer items-center gap-1 text-[24px] md:hidden'
                >
                    <Icon name='close' />
                </button>
            </div>
            <div className='flex flex-1 overflow-y-auto'>
                <div className='w-full items-start'>
                    {activeView ? (
                        activeView
                    ) : routesToDisplay.length > 0 ? (
                        <Panel animate={false} footer={path.length > 0 ? <PanelFooter /> : undefined}>
                            <ul className={cn('w-full space-y-2')}>
                                {routesToDisplay.map((route, index) => {
                                    return (
                                        <React.Fragment key={route.id}>
                                            <RouteMenuItem route={route} index={index} />
                                        </React.Fragment>
                                    );
                                })}
                            </ul>
                        </Panel>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export { Routes } from './constants';
export { HeaderButton } from './HeaderButton';
export { MenuProvider } from './MenuProvider';
